import { Seo } from "@/components/Seo";
import { Reveal, PillButton, Hairline } from "@/components/primitives";
import { StewardStoneNav } from "@/components/StewardStoneNav";

const SECTIONS = [
  { t: "Accountability & Decision Authority", d: "Mapping who owns which decisions, where authority is unclear, and where accountability gaps slow or distort execution." },
  { t: "Resource Allocation", d: "Examining how people, budget, and time are assigned against actual demand rather than historical habit." },
  { t: "Communication Structures", d: "Locating where handoffs, escalations, and information flow fail between roles, shifts, or departments." },
  { t: "Operational Bottlenecks", d: "Isolating the constraints that cap throughput and create backlog, rework, or delay." },
  { t: "Scalability & Continuity", d: "Assessing whether current processes remain reliable through growth, turnover, and disruption, and identifying their most consequential vulnerabilities." },
  { t: "Measurement", d: "Selecting and tracking measures that distinguish substantive operational improvement from fluctuations in reported performance." },
];

export default function OperationalStrategy() {
  return (
    <div className="glow-navy jmh-page">
      <Seo title="Operational Strategy — Steward & Stone" description="Steward & Stone Operational Strategy — organizational and operational assessment, workflow and gap analysis, systems redesign, and practical implementation planning. Broader than auditing." />
      <StewardStoneNav />

      <section className="text-center jmh-section jmh-page-opening">
        <Reveal>
          <p className="font-display uppercase tracking-luxe text-[10px] text-[var(--gold)]/80">Steward &amp; Stone</p>
          <h1 className="mt-3 font-serif2 text-4xl md:text-5xl text-[var(--bone)] leading-tight">Operational Strategy</h1>
          <p className="mt-6 mx-auto text-lg md:text-xl text-[var(--bone)]/85 leading-relaxed">
            Effective operational strategy begins with understanding how an organization functions in practice. We examine the relationship between its goals, decisions, resources, and daily workflows, then develop an improvement strategy grounded in the conditions leadership and employees must navigate.
          </p>
        </Reveal>
      </section>

      <section className="jmh-section">
        <div className="mx-auto jmh-content-shell">
          <Reveal><Hairline className="opacity-70 mb-12" /></Reveal>
          <div className="jmh-content-grid">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.05}>
                <div className="corner-frame px-7 py-8 h-full">
                  <h3 className="font-serif2 text-xl md:text-2xl text-[var(--bone)]">{s.t}</h3>
                  <p className="mt-4 text-sm md:text-base text-[var(--bone)]/75 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="text-center jmh-section">
        <Reveal className="mx-auto jmh-content-shell">
          <p className="text-[var(--bone)]/80 leading-relaxed">
            The review connects workflow, accountability, resource allocation, decision authority, and communication with the outcomes they produce. It considers operational constraints, continuity, and the capacity to adapt, using meaningful measures to determine whether proposed changes improve actual performance.
          </p>
        </Reveal>
        <Reveal delay={0.1} className="mt-8"><PillButton to="/steward-and-stone/assessment" testid="operational-inquiry">Request an Assessment</PillButton></Reveal>
      </section>
    </div>
  );
}
